import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {ALLOWED_ENTRY_TO_ACCORDION_COLOR} from 'notebook/notes/sections/EntrySection';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ColorSwatch} from 'ui-library/ColorSwatch';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export interface SectionColorPickerProps {
  selectedColor: string;
  onSelectColor: (color: string) => void;
}

function ColorPalette({selectedColor, onSelectColor}: SectionColorPickerProps): JSX.Element {
  const swatches = Object.entries(ALLOWED_ENTRY_TO_ACCORDION_COLOR).map(([color, hexvalue], index) => (
    <Col key={color} className={css(styles.swatchCol)}>
      <ColorSwatch
        fillColor={hexvalue}
        darkerBorder={color === selectedColor}
        onClick={() => {
          onSelectColor(color);
        }}
        dataTestAttrs={{component: 'SectionColorPicker', element: 'selector'}}
      />
    </Col>
  ));

  const colsPerRow = 8;
  const numRows = Math.ceil(swatches.length / colsPerRow);
  const rows = [...Array(numRows)].map((_, i) => (
    <Row key={i} className={css(styles.swatchRow)}>
      {swatches.slice(i * colsPerRow, (i + 1) * colsPerRow)}
    </Row>
  ));
  return (
    <div
      onClick={(e) => {
        // Prevents toggling the section header
        e.stopPropagation();
      }}
    >
      <b>Color palette</b>
      <div className={css(styles.popoverContent)}>{rows}</div>
    </div>
  );
}

/**
 * Renders a palette icon that opens up a color palette that can be clicked to select a new color for the section.
 *
 * @param selectedColor Currently selected color.
 * @param onSelectColor Callback when a color is selected.
 */
export default function SectionColorPicker({
  selectedColor,
  onSelectColor,
}: SectionColorPickerProps): JSX.Element {
  assert(selectedColor != null, 'Expected non-null color for swatch colorpicker');
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Popover
        interaction={FloatingInteraction.CLICK}
        renderContent={() => <ColorPalette selectedColor={selectedColor} onSelectColor={onSelectColor} />}
        placement={FloatingPlacement.BOTTOM_END}
      >
        <BenchlingIcon icon="palette" />
      </Popover>
    </div>
  );
}

const styles = StyleSheet.create({
  // .row has negative margin inside buffer tabs for reasons lost to antiquity.
  swatchRow: {
    margin: '0 0 5px 0',
  },
  swatchCol: {
    display: 'inline-block',
    paddingLeft: '0px',
    paddingRight: '0px',
    marginRight: '5px',
  },
  popoverContent: {
    padding: '10px',
  },
});
